// @ts-ignore
import { UseFormGetValues, ValidationRules } from 'react-hook-form';
import _ from 'lodash';
import { TFunction } from 'i18next';

export enum AvailableValidationRules {
  required = 'required',
  requiredIfNotEmpty = 'requiredIfNotEmpty',
  requiredIfEquals = 'requiredIfEquals',
  requiredFile = 'requiredFile',
  equals = 'equals',
  email = 'email',
  password = 'password',
  float = 'float',
  negative = 'negative',
}

export type ValidationRuleConfig = Array<
  | AvailableValidationRules
  | {
      rule: AvailableValidationRules;
      errorMessage: string;
      ifNotEmpty?: string[];
      ifEquals?: string[];
      compareValue?: string | ((v: string | number) => boolean);
      equals?: string;
    }
>;

type ValidationRulesDefiniton = (
  name?: string,
  message?: string,
  ifNotEmpty?: string[],
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  getValues?: UseFormGetValues<any>,
  equals?: string,
  ifEquals?: string[],
  compareValue?: string | ((v: string | number) => boolean)
) => ValidationRules;

// eslint-disable-next-line max-len
export type ValidationRulesDefinitionMap = Record<
  AvailableValidationRules,
  ValidationRulesDefiniton
>;

const availableValidationRulesDefinition = (t: TFunction): ValidationRulesDefinitionMap => ({
  required: (name?: string, message?: string): ValidationRules => ({
    required: { value: true, message: message || `${name} ${t('est requis')}` },
  }),
  requiredIfNotEmpty: (
    name?: string,
    message?: string,
    ifNotEmpty?: string[],
    // biome-ignore lint/suspicious/noExplicitAny: <explanation>
    getValues?: UseFormGetValues<any>
  ): ValidationRules => ({
    validate: {
      requiredIfNotEmpty: (data: any) => {
        const fieldsNotEmpty = ifNotEmpty
          ?.map((v) => {
            if (getValues) {
              const otherValue = getValues(v);
              if (otherValue && otherValue.trim() !== '') {
                return true;
              }
            }
            return false;
          })
          .filter((v) => v);
        if (
          (fieldsNotEmpty?.length || 0) > 0 &&
          (!data ||
            (data instanceof Array && data.length === 0) ||
            (data instanceof String && data.trim() === ''))
        ) {
          return message || `${name} est requis`;
        }
        return true;
      },
    },
  }),
  requiredIfEquals: (
    name?: string,
    message?: string,
    ifNotEmpty?: string[],
    // biome-ignore lint/suspicious/noExplicitAny: <explanation>
    getValues?: UseFormGetValues<any>,
    equals?: string,
    ifEquals?: string[],
    compareValue?: string | ((v: string | number) => boolean)
  ): ValidationRules => ({
    validate: {
      requiredIfEquals: (data: any) => {
        const fieldsEquals = ifEquals
          ?.map((v) => {
            if (getValues) {
              const otherValue = getValues(v);
              if (otherValue) {
                if (compareValue instanceof Function) {
                  return compareValue(otherValue);
                }
                if (otherValue.trim() === compareValue) {
                  return true;
                }
              }
            }
            return false;
          })
          .filter((v) => v);

        if (
          (fieldsEquals?.length || 0) > 0 &&
          (!data ||
            (data instanceof Array && data.length === 0) ||
            (data instanceof String && data.trim() === ''))
        ) {
          return message || `${name} est requis`;
        }
        return true;
      },
    },
  }),
  requiredFile: (name?: string, message?: string): ValidationRules => ({
    validate: {
      requiredFile: (data: any) => {
        if (
          !data ||
          (data instanceof Array && data.length === 0) ||
          (data instanceof String && data.trim() === '')
        ) {
          return message || `${name} est requis`;
        }
        return true;
      },
    },
  }),
  equals: (
    name?: string,
    message?: string,
    ifNotEmpty?: string[],
    // biome-ignore lint/suspicious/noExplicitAny: <explanation>
    getValues?: UseFormGetValues<any>,
    equals?: string
  ): ValidationRules => ({
    validate: {
      equals: (data: string | undefined) => {
        let otherValue;
        if (getValues && equals) {
          otherValue = getValues(equals);
        }
        if (data !== otherValue) {
          return message || `${name} doit correspondre`;
        }
        return true;
      },
    },
  }),
  email: (name?: string, message?: string): ValidationRules => ({
    pattern: {
      value:
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
      message: message || `Le champ ${name} n'est pas valide`,
    },
  }),
  password: (name?: string, message?: string): ValidationRules => ({
    validate: {
      password: (data: string | undefined) => {
        if (data !== undefined && data.trim() !== '' && data.length < 8) {
          return message || `${name} doit avoir une longueur minimale de 8 caractères`;
        }
        if (data !== undefined && data.trim() !== '') {
          const hasUpperCase = /[A-Z]/.test(data.trim());
          const hasLowerCase = /[a-z]/.test(data.trim());
          const hasNumbers = /\d/.test(data.trim());
          const hasNonalphas = /\W/.test(data.trim());
          if (hasUpperCase && hasLowerCase && hasNumbers && hasNonalphas) {
            return true;
          } else {
            return (
              message ||
              `${name} doit contenir au minimum un chiffre, une minuscule, une majuscule et un caractère special de la liste suivante : ^ $ * . [ ] { } ( ) ? - " ! @ # % & / \\ , > < ' : ; | _ ~ \` + =`
            );
          }
        }
        return true;
      },
    },
  }),
  float: (name?: string, message?: string): ValidationRules => ({
    validate: {
      float: (data: string | undefined) => {
        if (data && Number.isNaN(Number(data))) {
          return message || `${name} ne doit pas contenir de lettres`;
        }
        return true;
      },
    },
  }),
  negative: (name?: string, message?: string): ValidationRules => ({
    validate: {
      negative: (data: string | undefined) => {
        if (Number(data) > 0) {
          return message || `${name} doit être nul ou inférieur a 0`;
        }
        return true;
      },
    },
  }),
  isSynipQRCode: (name?: string, message?: string): ValidationRules => ({
    validate: {
      isSynipQRCode: (data: string | undefined) => {
        if (data && data.indexOf('https://app.synip.fr/s/') <= -1) {
          return message || `Merci de bien vouloir scanner un QR code Synip`;
        }
        return true;
      },
    },
  }),
});

export default availableValidationRulesDefinition;

export const composeValidationRules = (
  rulesList: ValidationRuleConfig,
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  getValues: UseFormGetValues<any>,
  name?: string,
  t?: TFunction
): ValidationRules => {
  const validationRules: ValidationRules = {};

  rulesList.forEach((rule) => {
    let definition: ValidationRules = {};
    if (typeof rule === 'object') {
      definition = availableValidationRulesDefinition(t)[rule.rule](
        name,
        rule.errorMessage,
        rule.ifNotEmpty,
        getValues,
        rule.equals,
        rule.ifEquals,
        rule.compareValue
      );
    } else {
      definition = availableValidationRulesDefinition(t)[rule](name);
    }
    _.merge<ValidationRules, ValidationRules>(validationRules, definition);
  });

  return validationRules;
};

export const hasRequiredFields = (rules: ValidationRuleConfig) => {
  if (rules.indexOf(AvailableValidationRules.required) !== -1) {
    return true;
  }
  for (let i = 0; i < rules.length; i++) {
    if (_.isObject(rules[i])) {
      // @ts-ignore
      if (rules[i].rule === AvailableValidationRules.required) {
        return true;
      }
    }
  }
  return false;
};
